import { useState, useEffect } from "react";
import { Link } from "react-router";
import { motion } from "motion/react";
import { Calendar, Clock, ArrowRight, User, Tag, Search, Loader2, Star, ImageIcon, ChevronRight } from "lucide-react";
import { blogService, type BlogPost } from "../../lib/services/blogService";
import type { Models } from "appwrite";

type BlogDoc = BlogPost & Models.Document;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const blogCategories = ["All", "HVAC", "Refrigeration", "Heat Pump", "Electrical", "Engineering", "Case Study"];

const FALLBACK_IMG = "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=600";

import { SEO } from "./SEO";

export function BlogPage() {
  const [posts, setPosts] = useState<BlogDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    blogService.list()
      .then(setPosts)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Only show published posts
  const published = posts.filter((p) => p.status === "Published" || !p.status);

  const featuredPost = published.find((p) => p.featured === "true");
  const regularPosts = published.filter((p) => p.featured !== "true");

  const filtered = regularPosts.filter((p) => {
    const matchesCat = activeCategory === "All" || p.category === activeCategory;
    const matchesSearch =
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.excerpt ?? "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesSearch;
  });

  return (
    <div>
      <SEO
        title="Engineering Insights & Updates | Solace SMART Solutions Blog"
        description="Stay up to date with the latest HVAC trends, technical guides, industrial insights, and company news from our engineering experts."
      />
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-[#1a2d50] via-[#1f3561] to-[#243d6a] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#E9A319] rounded-full blur-3xl" />
        </div>
        <div className="max-w-screen-2xl mx-auto px-6 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center justify-center gap-2 text-sm mb-6 w-full">
            <Link to="/" className="text-white/50 hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-white/30" />
            <span className="text-[#E9A319]" style={{ fontWeight: 600 }}>Blog & Resources</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl md:text-5xl mt-3 mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
            Engineering Insights & Updates
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-blue-100 max-w-2xl mx-auto text-lg" style={{ lineHeight: 1.8 }}>
            Stay up to date with the latest HVAC trends, technical guides, industrial insights, and company news.
          </motion.p>
        </div>
      </section>

      {/* Loading state */}
      {loading && (
        <section className="py-20">
          <div className="max-w-screen-2xl mx-auto px-6 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#1a2d50]/40" />
          </div>
        </section>
      )}

      {/* Featured Post */}
      {!loading && featuredPost && (
        <section className="py-16">
          <div className="max-w-screen-2xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
              className="grid lg:grid-cols-2 gap-8 bg-white rounded-3xl overflow-hidden shadow-xl border border-border"
            >
              <div className="relative overflow-hidden h-64 lg:h-auto bg-gray-100">
                {featuredPost.imageUrl ? (
                  <img src={featuredPost.imageUrl} alt={featuredPost.title} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMG; }} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-16 h-16 text-gray-200" />
                  </div>
                )}
                <span className="absolute top-4 left-4 bg-[#E9A319] text-white px-4 py-1.5 rounded-full text-sm" style={{ fontWeight: 600 }}>
                  <Star className="inline w-3.5 h-3.5 mr-1 fill-white" />Featured
                </span>
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> {featuredPost.category}</span>
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {featuredPost.date}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {featuredPost.readTime}</span>
                </div>
                <h2 className="text-2xl md:text-3xl text-foreground mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                  {featuredPost.title}
                </h2>
                {featuredPost.excerpt && (
                  <p className="text-muted-foreground mb-6" style={{ lineHeight: 1.8 }}>{featuredPost.excerpt}</p>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-sm text-foreground" style={{ fontWeight: 500 }}>{featuredPost.author}</span>
                  </div>
                  <Link to={`/blog/${featuredPost.slug}`} className="text-[#E9A319] flex items-center gap-2 hover:gap-3 transition-all" style={{ fontWeight: 600 }}>
                    Read Article <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Filters - Sticky & Mobile Optimized */}
      {!loading && (
        <section className="sticky top-16 z-40 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b border-border mb-8 shadow-sm">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-3">
            <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-center justify-between">
              {/* Category Scroll Container */}
              <div className="flex gap-2 w-full md:w-auto overflow-x-auto pb-1 md:pb-0 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {blogCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all border shrink-0 ${activeCategory === cat ? "bg-[#1a2d50] border-[#1a2d50] text-white shadow-md" : "bg-white border-border text-[#1a2d50] hover:bg-muted hover:border-[#1a2d50]/30"}`}
                    style={{ fontWeight: 500 }}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* Search Bar */}
              <div className="relative w-full md:w-80 shrink-0">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-full bg-white border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#1a2d50]/20 focus:border-[#1a2d50]/50 shadow-sm transition-all"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Blog Grid */}
      {!loading && (
        <section className="pb-20">
          <div className="max-w-screen-2xl mx-auto px-6">
            {filtered.length > 0 ? (
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filtered.map((post) => (
                  <motion.article key={post.$id} variants={fadeUp} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#1a2d50]/5 hover:shadow-xl transition-all duration-300 group">
                    <div className="relative overflow-hidden h-48 bg-gray-100">
                      {post.imageUrl ? (
                        <img
                          src={post.imageUrl}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMG; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-12 h-12 text-gray-200" />
                        </div>
                      )}
                      <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-foreground px-3 py-1 rounded-full text-xs" style={{ fontWeight: 600 }}>
                        {post.category}
                      </span>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {post.date}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
                      </div>
                      <h3 className="text-[#1a2d50] mb-3 group-hover:text-[#E9A319] transition-colors" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, lineHeight: 1.4 }}>
                        {post.title}
                      </h3>
                      {post.excerpt && (
                        <p className="text-muted-foreground text-sm mb-4" style={{ lineHeight: 1.7 }}>{post.excerpt}</p>
                      )}
                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                            <User className="w-3 h-3 text-primary" />
                          </div>
                          <span className="text-xs text-muted-foreground">{post.author}</span>
                        </div>
                        <Link to={`/blog/${post.slug}`} className="text-[#E9A319] text-sm flex items-center gap-1 hover:gap-2 transition-all" style={{ fontWeight: 600 }}>
                          Read <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </motion.div>
            ) : (
              <div className="text-center py-20">
                <p className="text-muted-foreground text-lg">
                  {posts.length === 0 ? "No blog posts published yet." : "No articles found matching your criteria."}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Newsletter */}
      <section className="py-20 bg-[#f8f6f1]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl text-[#1a2d50] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>Stay in the Loop</h2>
          <p className="text-muted-foreground mb-8" style={{ lineHeight: 1.8 }}>
            Get weekly engineering tips, product reviews, exclusive offers, and early access to new technology guides — delivered straight to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
            <input
              type="email"
              placeholder="Enter your email address"
              className="flex-1 px-6 py-3.5 rounded-full bg-white border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <button className="bg-[#E9A319] text-white px-8 py-3.5 rounded-full hover:bg-[#d4920f] transition-all shadow-lg" style={{ fontWeight: 600 }}>
              Subscribe
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-4">No spam, ever. Unsubscribe at any time.</p>
        </div>
      </section>
    </div>
  );
}
